/**
 * Name + avatar, and nothing else. SPEC.md §6.3, §8.6, §19.
 *
 * The same form creates a table and joins one — both need exactly this
 * information, so there is one component and only the button label differs. §6.3
 *
 * Validation is the shared playerIdentitySchema: its messages are already written
 * for a player ("Please enter a name."), so nothing here composes copy of its
 * own. §35, §38
 */

import { useEffect, useId, useState } from 'react';
import {
  PLAYER_NAME_MAX_LENGTH,
  firstFreeAvatarKey,
  isAvatarTaken,
  playerIdentitySchema,
  type PlayerIdentityInput,
} from '@card-counter/shared';
import { Button } from '@/components/ui/Button';
import { AvatarPicker } from './AvatarPicker';

/** Where the character counter appears, close enough to the cap to matter. §8.6 */
const COUNTER_THRESHOLD = PLAYER_NAME_MAX_LENGTH - 5;

export interface JoinFormProps {
  submitLabel: string;
  submitting?: boolean;
  /** Copy from a failed create/join. Always already safe to display. §35 */
  error?: string | null;
  /**
   * Fired on every pick, for a screen that shows the choice somewhere this form
   * does not reach — the create screen enlarges it where its title used to be.
   *
   * Deliberately not fired for the initial default: a player who has picked
   * nothing has not picked the default, and a preview that jumped to it on
   * arrival would say they had. §6.3
   */
  onAvatarChange?(avatarKey: string): void;
  /**
   * Avatars already at the table, greyed out in the picker. §46
   *
   * Empty when creating, because there is no table yet to have taken any.
   */
  takenAvatarKeys?: readonly string[];
  onSubmit(identity: PlayerIdentityInput): void;
}

const NOTHING_TAKEN: readonly string[] = [];

export function JoinForm({
  submitLabel,
  submitting = false,
  error = null,
  onAvatarChange,
  takenAvatarKeys = NOTHING_TAKEN,
  onSubmit,
}: JoinFormProps) {
  const [name, setName] = useState('');
  /**
   * The first character nobody has, rather than a fixed default: a form that
   * opened on a taken avatar would open on a selection it would then refuse to
   * submit. Computed once, on mount, because it is an initial value and not a
   * running opinion — recomputing it would move a player's choice out from under
   * them the moment somebody else joined. §6.3, §46
   */
  const [avatarKey, setAvatarKey] = useState<string>(() => firstFreeAvatarKey(takenAvatarKeys));
  const [invalid, setInvalid] = useState<string | null>(null);

  /**
   * Somebody took the one this form was holding. §46
   *
   * The only way this fires is a failed submit — the taken list is fetched with
   * the table and does not stream — so it is the correction that follows "pick
   * another", and it makes the retry a single tap on the button rather than a
   * hunt for what changed.
   */
  useEffect(() => {
    if (!isAvatarTaken(avatarKey, takenAvatarKeys)) return;
    const free = firstFreeAvatarKey(takenAvatarKeys);
    setAvatarKey(free);
    onAvatarChange?.(free);
  }, [avatarKey, takenAvatarKeys, onAvatarChange]);

  const nameId = useId();
  const avatarLabelId = useId();
  const messageId = useId();

  const message = error ?? invalid;

  function submit(): void {
    const parsed = playerIdentitySchema.safeParse({ name, avatarKey });
    if (!parsed.success) {
      setInvalid(parsed.error.issues[0]?.message ?? null);
      return;
    }
    setInvalid(null);
    onSubmit(parsed.data);
  }

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      className="flex flex-col gap-5"
    >
      <div className="flex flex-col gap-2">
        <label htmlFor={nameId} className="text-lg font-semibold text-ink">
          What&apos;s your name?
        </label>
        <input
          id={nameId}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setInvalid(null);
          }}
          type="text"
          // Capped in the field as well as in the schema, so the ring stays
          // readable and the cap is felt rather than reported. §8.6
          maxLength={PLAYER_NAME_MAX_LENGTH}
          autoComplete="nickname"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          placeholder="Mike"
          disabled={submitting}
          aria-invalid={message !== null}
          aria-describedby={message !== null ? messageId : undefined}
          className="min-h-tap w-full rounded-control border border-line bg-raised px-4 text-lg text-ink placeholder:text-ink-faint disabled:opacity-60"
        />
        {name.length >= COUNTER_THRESHOLD ? (
          <p className="text-right text-xs text-ink-faint" data-numeric>
            {name.length}/{PLAYER_NAME_MAX_LENGTH}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <span id={avatarLabelId} className="text-lg font-semibold text-ink">
          Choose avatar
        </span>
        <AvatarPicker
          value={avatarKey}
          onChange={(key) => {
            setAvatarKey(key);
            onAvatarChange?.(key);
          }}
          labelId={avatarLabelId}
          disabled={submitting}
          takenKeys={takenAvatarKeys}
        />
      </div>

      {message !== null ? (
        <p id={messageId} role="alert" className="text-sm text-negative">
          {message}
        </p>
      ) : null}

      <Button type="submit" size="lg" fullWidth loading={submitting}>
        {submitLabel}
      </Button>
    </form>
  );
}
